import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Loader2, Sparkles, Target, Rocket, Check, ChevronRight, Lightbulb,
  ListChecks, Save, AlertTriangle, Play, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * GetItDoneModal — V2 AI-guided 5-step wizard.
 *
 * Steps (conversational, confirmable per step):
 *   1. Goal         — AI asks, user states the outcome
 *   2. Constraints  — AI probes deadline/energy/blockers
 *   3. Tools        — AI pre-selects 2–4 TEG capabilities; user toggles Run/Skip
 *   4. Workflow     — AI proposes 3–5 ordered steps using chosen tools
 *   5. Done         — AI summarises; user saves to memories (ai_memories)
 *
 * Backend: POST /api/get-it-done/turn   — per-step LLM call (GPT-4o)
 *          POST /api/get-it-done/complete — persist to ai_memories
 */

const STEP_META = [
  { key: 'goal',        label: 'Goal',        icon: Target,     desc: 'What outcome are you aiming at?' },
  { key: 'constraints', label: 'Constraints', icon: AlertTriangle, desc: 'What shapes the path?' },
  { key: 'tools',       label: 'Tools',       icon: Rocket,     desc: 'Pick capabilities to help you.' },
  { key: 'workflow',    label: 'Workflow',    icon: ListChecks, desc: 'Ordered steps to ship it.' },
  { key: 'done',        label: 'Done',        icon: Check,      desc: 'Summary goes to memories.' },
];

const API = process.env.REACT_APP_BACKEND_URL;

function StepHeader({ activeIdx }) {
  return (
    <ol className="flex items-center gap-1 mt-1" data-testid="gid-step-header">
      {STEP_META.map((s, i) => {
        const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'todo';
        return (
          <li key={s.key} className="flex items-center gap-1 flex-1">
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider orka-transition-colors ${
                state === 'active'
                  ? 'orka-bg-intent text-white'
                  : state === 'done'
                    ? 'orka-bg-success-soft'
                    : 'orka-surface-subtle orka-text-tertiary'
              }`}
              data-testid={`gid-step-${s.key}-pill`}
              data-state={state}
            >
              <span className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-black/10 text-[10px]">{i + 1}</span>
              {s.label}
            </div>
            {i < STEP_META.length - 1 && <div className={`flex-1 h-px ${state === 'done' ? 'orka-bg-success-soft' : 'orka-border-subtle border-t'}`} />}
          </li>
        );
      })}
    </ol>
  );
}

export default function GetItDoneModal({ open, onOpenChange, task, onUpdateTask }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [assistantPrompt, setAssistantPrompt] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolPicks, setToolPicks] = useState([]); // [{capability_id, reason, action}]
  const [workflow, setWorkflow] = useState(null); // {steps, expected_output}
  const [finalSummary, setFinalSummary] = useState('');
  const [transcript, setTranscript] = useState([]); // [{role, content, step}]
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const sessionId = useMemo(() => `${task?.task_id || 'notask'}-${Date.now()}`, [task?.task_id, open]);
  const activeStep = STEP_META[activeIdx];

  // Boot the first step when modal opens.
  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    setUserAnswer('');
    setToolPicks([]);
    setWorkflow(null);
    setFinalSummary('');
    setTranscript([]);
    setAssistantPrompt('');
    // Fire the first 'goal' turn with empty user_answer to kick things off.
    runTurn('goal', '', []);
    setTimeout(() => inputRef.current?.focus(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.task_id]);

  const runTurn = async (step, answerText, runningTranscript) => {
    if (!task) return;
    setBusy(true);
    setAssistantPrompt('');
    try {
      const res = await axios.post(`${API}/api/get-it-done/turn`, {
        session_id: sessionId,
        step,
        task: {
          task_id: task.task_id,
          task_title: task.task_title || task.title,
          task_description: task.task_description || task.description,
          task_priority: task.task_priority,
          task_status: task.task_status,
          work_area_name: task.work_area_name,
          related_epic_name: task.related_epic_name,
        },
        user_answer: answerText,
        transcript: runningTranscript,
      }, { withCredentials: true });

      const { reply, reply_kind, payload } = res.data;
      setAssistantPrompt(reply_kind === 'text' ? reply : (payload?.intro || payload?.expected_output || reply));

      if (step === 'tools') {
        const picks = Array.isArray(payload?.picks) ? payload.picks.map((p) => ({ ...p, action: 'run' })) : [];
        setToolPicks(picks);
      } else if (step === 'workflow') {
        setWorkflow(payload || null);
      } else if (step === 'done') {
        setFinalSummary(reply);
      }

      setTranscript((t) => [...t, { role: 'assistant', content: reply, step }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`AI error: ${msg}`);
      setAssistantPrompt(`(Could not reach AI: ${msg})`);
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    if (activeIdx >= STEP_META.length - 1) return;
    const trimmed = (userAnswer || '').trim();
    const newTranscript = trimmed
      ? [...transcript, { role: 'user', content: trimmed, step: activeStep.key }]
      : transcript;
    setTranscript(newTranscript);
    setUserAnswer('');
    const nextIdx = activeIdx + 1;
    setActiveIdx(nextIdx);
    await runTurn(STEP_META[nextIdx].key, trimmed, newTranscript);
  };

  const back = () => {
    if (activeIdx === 0) return;
    setActiveIdx((i) => i - 1);
  };

  const toggleTool = (capabilityId) => {
    setToolPicks((picks) => picks.map((p) => p.capability_id === capabilityId ? { ...p, action: p.action === 'run' ? 'skip' : 'run' } : p));
  };

  const saveToMemories = async () => {
    if (!finalSummary || !task) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/get-it-done/complete`, {
        session_id: sessionId,
        task: {
          task_id: task.task_id,
          task_title: task.task_title || task.title,
        },
        summary: finalSummary,
        transcript,
        selected_tools: toolPicks.filter((p) => p.action === 'run').map((p) => p.capability_id),
        workflow_steps: workflow?.steps || [],
      }, { withCredentials: true });
      toast.success(`Saved to memories (${res.data.memory_id})`);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Could not save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="get-it-done-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 orka-text-intent" />
            Get it done
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 min-w-0">
            <span className="truncate">{task.task_title || task.title}</span>
            {task.task_priority != null && (
              <Badge variant="outline" className="text-[10px] ml-auto shrink-0">P{task.task_priority}</Badge>
            )}
          </DialogDescription>
          <StepHeader activeIdx={activeIdx} />
        </DialogHeader>

        {/* Assistant prompt for current step */}
        <div className="mt-2 p-3 rounded-xl border orka-border-subtle orka-bg-intent-soft text-xs leading-relaxed orka-text-primary min-h-[56px] whitespace-pre-line" data-testid="gid-assistant-prompt">
          {busy && !assistantPrompt && <span className="flex items-center gap-1.5 orka-text-tertiary"><Loader2 className="w-3 h-3 animate-spin" /> thinking…</span>}
          {!busy && !assistantPrompt && <span className="orka-text-tertiary">Ready.</span>}
          {assistantPrompt}
        </div>

        {/* Step body */}
        {activeStep.key === 'goal' || activeStep.key === 'constraints' ? (
          <Textarea
            ref={inputRef}
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); next(); } }}
            placeholder={activeStep.desc}
            rows={3}
            className="text-xs resize-none mt-2"
            data-testid={`gid-answer-${activeStep.key}`}
          />
        ) : null}

        {activeStep.key === 'tools' ? (
          <div className="mt-2 space-y-2" data-testid="gid-tools-picks">
            {toolPicks.length === 0 && !busy && (
              <div className="text-xs orka-text-tertiary">Orka will pick 2–4 capabilities once the previous answers are captured.</div>
            )}
            {toolPicks.map((p) => (
              <div key={p.capability_id} className={`flex items-start gap-3 p-3 rounded-xl border orka-transition-colors ${p.action === 'run' ? 'orka-border-strong orka-bg-info-soft' : 'orka-border-subtle orka-surface-subtle opacity-70'}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] orka-text-primary font-semibold">{p.capability_id}</div>
                  <div className="text-[11px] orka-text-tertiary leading-snug mt-0.5">{p.reason}</div>
                </div>
                <Button
                  size="sm"
                  variant={p.action === 'run' ? 'default' : 'outline'}
                  onClick={() => toggleTool(p.capability_id)}
                  data-testid={`gid-tool-toggle-${p.capability_id}`}
                  className="shrink-0 text-[11px] h-7 px-2.5"
                >
                  {p.action === 'run' ? <><Play className="w-3 h-3 mr-1" />Run</> : <><Ban className="w-3 h-3 mr-1" />Skip</>}
                </Button>
              </div>
            ))}
            <Textarea
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Want to add a capability Orka missed? Describe it here."
              rows={2}
              className="text-xs resize-none"
              data-testid="gid-answer-tools"
            />
          </div>
        ) : null}

        {activeStep.key === 'workflow' ? (
          <div className="mt-2 space-y-2" data-testid="gid-workflow-preview">
            {workflow?.steps?.length ? (
              <ol className="space-y-1.5">
                {workflow.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg border orka-border-subtle orka-surface-raised">
                    <span className="w-5 h-5 shrink-0 rounded-full orka-bg-intent text-white text-[10px] font-bold grid place-items-center">{s.order ?? i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs orka-text-primary">{s.what}</div>
                      {Array.isArray(s.uses) && s.uses.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.uses.map((u) => <span key={u} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full orka-bg-info-soft">{u}</span>)}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : busy ? (
              <div className="text-xs orka-text-tertiary flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> drafting workflow…</div>
            ) : null}
            {workflow?.expected_output && (
              <div className="text-[11px] orka-text-tertiary italic">Expected output: {workflow.expected_output}</div>
            )}
            <Textarea
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Any tweaks before locking it in?"
              rows={2}
              className="text-xs resize-none"
              data-testid="gid-answer-workflow"
            />
          </div>
        ) : null}

        {activeStep.key === 'done' ? (
          <div className="mt-2 space-y-2" data-testid="gid-done-body">
            <Textarea
              value={finalSummary}
              onChange={(e) => setFinalSummary(e.target.value)}
              placeholder="Summary Orka drafted for your memories…"
              rows={5}
              className="text-xs resize-y"
              data-testid="gid-final-summary"
            />
            <div className="text-[10px] orka-text-tertiary flex items-center gap-1.5">
              <Lightbulb className="w-3 h-3" />
              This summary is embedded and saved to <code className="font-mono">ai_memories</code> with <code className="font-mono">memory_type="workflow_memory"</code> and links back to this task.
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t orka-border-subtle">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="gid-close">
            Close
          </Button>
          <div className="flex items-center gap-2">
            {activeIdx > 0 && (
              <Button variant="outline" size="sm" onClick={back} disabled={busy || saving} data-testid="gid-back">
                Back
              </Button>
            )}
            {activeIdx < STEP_META.length - 1 ? (
              <Button size="sm" onClick={next} disabled={busy} data-testid="gid-next">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
                Next: {STEP_META[activeIdx + 1].label}
              </Button>
            ) : (
              <Button size="sm" onClick={saveToMemories} disabled={saving || !finalSummary?.trim()} data-testid="gid-save-memory">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save to memories
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
